export type ChipColor = "default" | "primary" | "secondary" | "info" | "success" | "warning" | "error";

/** Colour for a duplicate-company match score in [0, 1]: likely, maybe, unlikely. */
export function similarityColor(score: number): ChipColor {
  if (score >= 0.85) return "success";
  if (score >= 0.7) return "warning";
  return "default";
}

/** Colour for a news sentiment score in [-1, 1]; mildly negative still warns. */
export function sentimentColor(score: number | null | undefined): ChipColor {
  if (score == null) return "default";
  if (score >= 0.25) return "success";
  if (score <= -0.25) return "error";
  if (score < 0) return "warning";
  return "default";
}

// A background job's status (import, export, purchase, agent run); in-flight ones spin.
const JOB_STATUS_COLORS: Record<string, ChipColor> = {
  pending: "default",
  queued: "default",
  running: "info",
  completed: "success",
  succeeded: "success",
  partial: "warning",
  failed: "error",
  cancelled: "default",
};

// How a recurring job's tick ended (periodic_ticks.outcome).
const TICK_OUTCOME_COLORS: Record<string, StatusStyle> = {
  ran: { color: "success", label: "Ran" },
  off: { color: "default", label: "Switched off" },
  failed: "error",
  stopped: { color: "default", label: "Chain ended" },
};

const ROLE_COLORS: Record<string, ChipColor> = {
  admin: "error",
  member: "primary",
  crm_reader: "info",
  user: "primary",
  publisher: "secondary",
  developer: "info",
};

/** Colour for a global or organization role. */
export function roleColor(role: string): ChipColor {
  return ROLE_COLORS[role] ?? "default";
}

// A prospect order's lifecycle (ProspectOrderStatus in svc-backend).
const ORDER_STATUS_COLORS: Record<string, ChipColor> = {
  new: "default",
  triaged: "info",
  in_progress: "primary",
  delivered: "success",
  closed: "default",
  cancelled: "error",
};

/** A status's chip colour, and its label when humanizing the value isn't enough. */
export type StatusStyle = ChipColor | { color: ChipColor; label: string };

// A reviewer's decision on agent work (agent_feedback.decision).
const FEEDBACK_DECISION_COLORS: Record<string, ChipColor> = {
  approved: "success",
  accepted: "success",
  rejected: "error",
  disqualified: "error",
  retracted: "default",
};

// How an AI field was (or wasn't) generated. Every method that isn't a
// `skipped_*` one produced a value; pass it through `aiMethodStatus` first.
const AI_METHOD_COLORS: Record<string, ChipColor> = {
  generated: "success",
  skipped_llm_error: "error",
  skipped_json_parse_failed: "error",
  skipped_rejected: "warning",
};

/** The `ai_method` registry value for an AI generation method name. */
export function aiMethodStatus(method: string): string {
  return method.startsWith("skipped_") ? method : "generated";
}

const TENDER_NOTICE_TYPE_COLORS: Record<string, StatusStyle> = {
  competition: "primary",
  award: "success",
  modification: "warning",
  pin: { color: "warning", label: "PIN" },
};

/** A regulatory filing's normalized status, from application through hearing to operation. */
const REGULATORY_FILING_STATUS_COLORS: Record<string, ChipColor> = {
  pending: "default",
  hearing: "warning",
  granted: "primary",
  under_construction: "primary",
  operational: "success",
  denied: "error",
  withdrawn: "error",
  other: "default",
};

// Where a reviewed row (a tenant's prospect, a signal) stands with its
// reviewers (`ReviewStatusChips`): the review states as they read on a list
// entry, plus the row's own flags.
const PROSPECT_STATUS: Record<string, StatusStyle> = {
  awaiting_approval: { color: "warning", label: "Waiting to continue" },
  pending: { color: "info", label: "To review" },
  accepted: { color: "success", label: "Pursuing" },
  disqualified: { color: "default", label: "Disqualified" },
  done: { color: "success", label: "Done" },
  back_from_snooze: { color: "info", label: "Back from snooze" },
  snoozed: { color: "default", label: "Snoozed" },
  reopened: { color: "info", label: "Reopened" },
  new_hits: { color: "warning", label: "New hits" },
};

// What prospected a company (`prospect_hits.kind`).
const PROSPECT_HIT_KIND: Record<string, StatusStyle> = {
  signal: { color: "warning", label: "Signal" },
  agent: { color: "primary", label: "Agent" },
  workflow: { color: "info", label: "Workflow" },
  manual: { color: "default", label: "Added by hand" },
  mcp: { color: "default", label: "MCP" },
  bulk_filter: { color: "default", label: "Bulk add" },
};

// The research agent's verdict on a signal (`ResearchVerdictChip`).
const RESEARCH_VERDICT: Record<string, StatusStyle> = {
  verified: "success",
  qualified: "info",
  disqualified: "error",
  inconclusive: "default",
  duplicate: "default",
};

// How carefully a signal brief must be handled (`SignalBriefPanel`).
const SIGNAL_SENSITIVITY: Record<string, StatusStyle> = {
  high: "error",
  medium: "warning",
  low: "default",
};

// What a person's profile says about their job search (`ContactAboutCard`).
const CONTACT_SIGNAL: Record<string, StatusStyle> = {
  open_to_work: { color: "success", label: "Open to work" },
};

// Who an inference instance serves and how its traffic is metered.
const INFERENCE_ROUTING: Record<string, StatusStyle> = {
  bulk_only: { color: "info", label: "Bulk only" },
  interactive_only: { color: "warning", label: "Interactive only" },
  self_limited: { color: "default", label: "Self-limited" },
  decision: { color: "secondary", label: "Decision model" },
};

// A rented GPU box (inference_rentals.status).
const INFERENCE_RENTAL: Record<string, StatusStyle> = {
  creating: { color: "info", label: "Renting" },
  booting: { color: "info", label: "Booting" },
  ready: { color: "success", label: "Ready" },
  destroying: { color: "warning", label: "Destroying" },
  destroyed: "default",
  failed: "error",
};

/**
 * Every status vocabulary the app shows as a chip, by kind. Add a kind here
 * rather than a colour map next to the page that shows it.
 */
const STATUS_REGISTRY = {
  job: JOB_STATUS_COLORS,
  tick: TICK_OUTCOME_COLORS,
  order: ORDER_STATUS_COLORS,
  feedback_decision: FEEDBACK_DECISION_COLORS,
  ai_method: AI_METHOD_COLORS,
  tender_notice_type: TENDER_NOTICE_TYPE_COLORS,
  regulatory_filing: REGULATORY_FILING_STATUS_COLORS,
  prospect_status: PROSPECT_STATUS,
  prospect_hit_kind: PROSPECT_HIT_KIND,
  research_verdict: RESEARCH_VERDICT,
  signal_sensitivity: SIGNAL_SENSITIVITY,
  contact_signal: CONTACT_SIGNAL,
  inference_routing: INFERENCE_ROUTING,
  inference_rental: INFERENCE_RENTAL,
} satisfies Record<string, Record<string, StatusStyle>>;

export type StatusChipKind = keyof typeof STATUS_REGISTRY;

/** The colour and label for `value` in the `kind` vocabulary; unknown values are grey. */
export function statusStyle(kind: StatusChipKind, value: string): { color: ChipColor; label?: string } {
  const style = (STATUS_REGISTRY[kind] as Record<string, StatusStyle>)[value];
  if (style == null) return { color: "default" };
  return typeof style === "string" ? { color: style } : style;
}

/** Colour for a tender's notice type (competition, award, ...). */
export function tenderNoticeTypeColor(noticeType: string): ChipColor {
  return statusStyle("tender_notice_type", noticeType).color;
}
